import BugsnagPerformance from '@bugsnag/browser-performance';
import Bugsnag from '@bugsnag/js';
import { NamedSpanQuery } from '@bugsnag/plugin-named-spans';
import { chain, router as nativeRouter } from '@leprecoins/effector';
import { SOCKET_STATUSES } from '@leprecoins/effector-sockets';
import { combine, createEffect, createStore, sample } from 'effector';
import { equals } from 'patronum';
import ym from 'react-yandex-metrika';
import {
	AuthDto,
	authModel,
	createGetAccountQuery,
	createGetCashdeskQuery,
	logoutMutation,
	receiveConnection,
	socket,
	USER_ROLES,
} from 'shared/api';
import { BUG_SNAG_ENABLED, YANDEX_METRIKA_ENABLED } from 'shared/config';
import { createModalSwitcher } from 'shared/lib';
import { router } from 'shared/routing';

const switcher = createModalSwitcher();

const getAccountQuery = createGetAccountQuery();

const getCashdeskQuery = createGetCashdeskQuery();

const $profile = createStore<AuthDto | null>(null);

const $role = $profile.map((profile) => profile?.role ?? null);

const $id = $profile.map((profile) => profile?.id ?? null);

const $name = $profile.map((profile) => profile?.name ?? null);

const $cashdesk = getCashdeskQuery.$data;

const $shortName = $name.map(
	(name) =>
		name
			?.trim()
			.replace(/\s+/g, ' ')
			.split(' ')
			.slice(0, 2)
			.map((s) => s.at(0)?.toUpperCase())
			.join('') ?? null,
);

const $accountId = $profile.map((profile) => profile?.accountId ?? null);

const $account = getAccountQuery.$data;

const $canCreateSubAccounts = $account.map(
	(account) => account?.permissions.canCreateSubAccounts ?? false,
);

const $canWriteUsers = $account.map((account) => account?.permissions.canWriteUsers ?? false);

const $canEditLimits = $account.map((account) => account?.permissions.canEditLimits ?? false);

const $canEditGameSettings = $account.map(
	(account) => account?.permissions.canEditGameSettings ?? false,
);

const $canWriteCashdesks = $account.map(
	(account) => account?.permissions.canWriteCashdesks ?? false,
);

const $canBlockGames = $account.map((account) => account?.permissions.canBlockGames ?? false);

const $permissions = combine({
	canCreateSubAccounts: $canCreateSubAccounts,
	canWriteUsers: $canWriteUsers,
	canEditLimits: $canEditLimits,
	canEditGameSettings: $canEditGameSettings,
	canWriteCashdesks: $canWriteCashdesks,
	canBlockGames: $canBlockGames,
});

const logout = logoutMutation.start;

chain(authModel.authorized, $profile);

sample({
	clock: authModel.authorized,
	fn: ({ accountId }) => accountId,
	target: getAccountQuery.start,
});

sample({
	clock: authModel.authorized,
	filter: ({ cashdeskId }) => cashdeskId !== null,
	fn: ({ cashdeskId }) => cashdeskId!,
	target: getCashdeskQuery.start,
});

sample({
	clock: authModel.authorized,
	filter: ({ role }) => role === USER_ROLES.cashier,
	target: socket.connect,
});

sample({
	clock: authModel.unauthorized,
	filter: equals(socket.$status, SOCKET_STATUSES.connected),
	target: socket.disconnect,
});

chain(router.$path, switcher.disable);

chain(logoutMutation.finished.success, nativeRouter.reload);

const bindUserToBugsnagFx = createEffect((user: AuthDto) => {
	Bugsnag.setUser(user.id.toString(), null, user.name);

	Bugsnag.addMetadata('user-information', {
		id: user.id,
		name: user.name,
		role: user.role,
		accountId: user.accountId,
		cashdeskId: user.cashdeskId,
	});
});

if (BUG_SNAG_ENABLED) {
	sample({
		clock: socket.receiveAny.failure,
		fn: (error) => new Error(JSON.stringify(error, null, 2)),
		target: createEffect(Bugsnag.notify),
	});

	sample({
		clock: authModel.authorized,
		target: bindUserToBugsnagFx,
	});

	sample({
		clock: authModel.unauthorized,
		target: createEffect(() => {
			Bugsnag.setUser();
		}),
	});

	sample({
		clock: socket.connect,
		target: createEffect(() => BugsnagPerformance.startSpan('connection')),
	});

	sample({
		clock: receiveConnection.success,
		target: createEffect(() =>
			BugsnagPerformance.getSpanControls(new NamedSpanQuery('connection'))?.end(),
		),
	});
}

const bindUserToYandexMetrikaFx = createEffect((user: AuthDto) => ym('userParams', user));

if (YANDEX_METRIKA_ENABLED) {
	sample({
		clock: authModel.authorized,
		target: bindUserToYandexMetrikaFx,
	});
}

export const viewerModel = {
	switcher,
	getAccountQuery,
	getCashdeskQuery,
	$profile,
	$id,
	$name,
	$shortName,
	$accountId,
	$account,
	$canCreateSubAccounts,
	$canWriteUsers,
	$canEditLimits,
	$canEditGameSettings,
	$canWriteCashdesks,
	$canBlockGames,
	$permissions,
	$cashdesk,
	$role,
	logout,
};
